/**
 * React Query hooks for map tiles.
 * React Query hooks for the corresponding entity.from mapSlice.ts.
 */

import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { bbox, BBox, FeatureCollection } from "@turf/turf";
import { queryKeys } from "common/queryKeys";
import { API } from "common/utils";
import { useOnlineStatus } from "common/utils/useOnlineStatus";
import { useGuardedMutation } from "model/useGuardedMutation";
import { useMemo } from "react";

type Visibility = "none" | "visible";
type Scheme = "xyz" | "tms";

export type Tile = {
  guid: string;
  name: string;
  type: string;
  tiles: string[];
  scheme: Scheme;
  bounds: BBox;
  tileSize: number;
  minzoom: number;
  maxzoom: number;
  visibility: Visibility;
  field?: string;
};

export type TileResponse = {
  guid: string;
  name: string;
  bucket: string;
  prefix: string;
  scheme: Scheme;
  geom: FeatureCollection;
  tileSize: number;
  minZoom: number;
  maxZoom: number;
  field?: string;
};

const tileApi = new API<TileResponse>("tiles");

/**
 * Process tile response into Tile format
 * Constructs tile URL template for MapLibre to fetch raster tiles
 */
function processResponse(tiles: Array<TileResponse>): Array<Tile> {
  return tiles.map((tile) => ({
    ...tile,
    type: "raster",
    tiles: [
      `${import.meta.env.VITE_API_ENDPOINT}/v1/tiles/${
        tile.guid
      }/{z}/{x}/{y}.png`,
    ],
    bounds: bbox(tile.geom),
    minzoom: tile.minZoom,
    maxzoom: tile.maxZoom,
    visibility: "visible" as Visibility,
  }));
}

// Tile metadata rarely changes, so use a long staleTime to prevent unnecessary re-fetches.
// Tiles are pre-fetched during initialization, so this avoids duplicate requests.
const TILE_STALE_TIME = 5 * 60 * 1000; // 5 minutes

/**
 * Fetch a single tile by guid
 */
export function useTile(guid: string | undefined, enabled = true) {
  const online = useOnlineStatus();

  return useQuery({
    queryKey: queryKeys.tiles.detail(guid ?? ""),
    queryFn: async () => {
      if (!guid) {
        throw new Error("Tile guid is required");
      }
      const response = await tileApi.getOne(guid);
      const processed = processResponse([response]);
      return processed[0];
    },
    // Offline: serve cached metadata only; never refetch (avoids 400s for
    // deleted guids and pointless network errors when disconnected).
    enabled: enabled && !!guid && online,
    staleTime: TILE_STALE_TIME,
  });
}

/**
 * Fetch multiple tiles by their IDs.
 * Uses staleTime to prevent re-fetching tiles that were pre-fetched during initialization.
 */
export function useTilesByIds(tileIds: string[] | undefined, enabled = true) {
  const online = useOnlineStatus();

  const queries = useQueries({
    queries: (tileIds || []).map((tileId) => ({
      queryKey: queryKeys.tiles.detail(tileId),
      queryFn: async () => {
        const response = await tileApi.getOne(tileId);
        const processed = processResponse([response]);
        return processed[0];
      },
      enabled: enabled && tileIds !== undefined && tileIds.length > 0 && online,
      staleTime: TILE_STALE_TIME,
    })),
  });

  const tiles = queries
    .map((query) => query.data)
    .filter((tile): tile is Tile => tile !== undefined);

  const isLoading = queries.some((query) => query.isLoading);
  const isError = queries.some((query) => query.isError);

  return {
    data: tiles,
    isLoading,
    isError,
    queries,
  };
}

/**
 * Fetch tiles for a field
 */
export function useTilesForField(fieldTileIds: string[] | undefined) {
  return useTilesByIds(fieldTileIds);
}

/**
 * Get latest tile for each field from array of tiles
 */
export function getLatestTilesByFieldFromArray(
  tiles: Tile[]
): Record<string, Tile> {
  const tilesByField: Record<string, Tile[]> = {};

  tiles.forEach((tile) => {
    if (tile.field) {
      if (!tilesByField[tile.field]) {
        tilesByField[tile.field] = [];
      }
      tilesByField[tile.field].push(tile);
    }
  });

  const latestByField: Record<string, Tile> = {};
  Object.entries(tilesByField).forEach(([fieldId, fieldTiles]) => {
    // Tiles are sorted by name (timestamp), first one is latest
    if (fieldTiles.length > 0) {
      latestByField[fieldId] = fieldTiles[0];
    }
  });

  return latestByField;
}

/**
 * Get field tiles (tiles that have a field property)
 */
export function getFieldTilesFromArray(tiles: Tile[]): Tile[] {
  return tiles.filter((t) => t.field);
}

/**
 * Get field tiles excluding deleted boundaries
 */
export function getFieldTilesForNotDeletedBoundariesFromArray(
  tiles: Tile[],
  tmpDeletedFeatures: string[]
): Tile[] {
  return tiles.filter(
    (t) => t.field && tmpDeletedFeatures.indexOf(t.field) === -1
  );
}

/**
 * Hook to get field tiles for not deleted boundaries
 */
export function useFieldTilesForNotDeletedBoundaries(
  tileIds: string[] | undefined,
  tmpDeletedFeatures: string[]
) {
  const { data: tiles = [], isLoading, isError } = useTilesByIds(tileIds);

  const filteredTiles = useMemo(() => {
    return getFieldTilesForNotDeletedBoundariesFromArray(
      tiles,
      tmpDeletedFeatures
    );
  }, [tiles, tmpDeletedFeatures]);

  return {
    data: filteredTiles,
    isLoading,
    isError,
  };
}

/**
 * Delete all tiles for a field
 */
export function useDeleteTilesForField() {
  const queryClient = useQueryClient();

  return useGuardedMutation({
    mutationFn: async (tileIds: string[]) => {
      await Promise.all(tileIds.map((t) => tileApi.deleteOne(t)));
      return tileIds;
    },
    onSuccess: async (deletedTileIds) => {
      deletedTileIds.forEach((tileId) => {
        queryClient.removeQueries({ queryKey: queryKeys.tiles.detail(tileId) });
      });
      await queryClient.invalidateQueries({ queryKey: queryKeys.tiles.all });
    },
  });
}

/**
 * Set tile visibility in cache
 */
export function setTileVisibilityQuery(
  tileId: string,
  visibility: Visibility,
  queryClient: ReturnType<typeof useQueryClient>
): void {
  const currentTile = queryClient.getQueryData<Tile>(
    queryKeys.tiles.detail(tileId)
  );

  if (currentTile) {
    queryClient.setQueryData<Tile>(queryKeys.tiles.detail(tileId), {
      ...currentTile,
      visibility,
    });
  }
}

/**
 * Hook wrapper for setTileVisibilityQuery
 */
export function useSetTileVisibility() {
  const queryClient = useQueryClient();
  return (tileId: string, visibility: Visibility) =>
    setTileVisibilityQuery(tileId, visibility, queryClient);
}

// Maximum concurrent tile requests to avoid overwhelming the browser
const MAX_CONCURRENT_TILE_REQUESTS = 6;

/**
 * Fetch tiles by IDs and store in query cache.
 * Used during initialization to pre-populate the cache.
 * Batches requests to limit concurrent connections and avoid ERR_INSUFFICIENT_RESOURCES.
 */
export async function fetchTilesByIdsQuery(
  tileIds: string[],
  queryClient: ReturnType<typeof useQueryClient>
): Promise<Tile[]> {
  if (!tileIds || tileIds.length === 0) return [];

  const results: Tile[] = [];

  // Process tiles in batches to limit concurrent requests
  for (let i = 0; i < tileIds.length; i += MAX_CONCURRENT_TILE_REQUESTS) {
    const batch = tileIds.slice(i, i + MAX_CONCURRENT_TILE_REQUESTS);
    const batchResults = await Promise.all(
      batch.map(async (tileId) => {
        try {
          const response = await tileApi.getOne(tileId);
          const processed = processResponse([response]);
          const tile = processed[0];
          // Store in cache with long staleTime
          queryClient.setQueryData(queryKeys.tiles.detail(tileId), tile);
          return tile;
        } catch (error) {
          console.error(`Failed to fetch tile ${tileId}:`, error);
          return null;
        }
      })
    );
    results.push(...batchResults.filter((t): t is Tile => t !== null));
  }

  return results;
}

/**
 * Helper function to reset tiles query cache
 */
export function resetTilesQueryCache(
  queryClient: ReturnType<typeof useQueryClient>
): void {
  queryClient.removeQueries({ queryKey: queryKeys.tiles.all });
}

/**
 * Hook wrapper for resetTilesQueryCache
 */
export function useResetTilesQueryCache() {
  const queryClient = useQueryClient();
  return () => resetTilesQueryCache(queryClient);
}

export { tileApi };
